/**
 * Mobile Inspection View
 *
 * Touch-optimized inspection interface with:
 * - Swipeable category navigation
 * - Large touch targets (min 44x44px)
 * - Pull-to-refresh
 * - Offline-capable (optimistic updates)
 * - Quick condition selection
 */

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  Paper,
  AppBar,
  Toolbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SaveIcon from '@mui/icons-material/Save';
import WarningIcon from '@mui/icons-material/Warning';
import {
  TouchConditionSelector,
  ConditionBadge,
} from '@/components/inspection/TouchConditionSelector';
import { PhotoCapture, PhotoPreview } from '@/components/inspection/PhotoCapture';
import { useNotify } from '@/stores/notificationStore';
import {
  useInspection,
  useUpdateItemCondition,
  useAddPhoto,
  type InspectionItem,
} from '@/hooks/useInspections';
import type { InspectionItemCondition } from '@/types';

// Category icons
import BuildIcon from '@mui/icons-material/Build';
import SpeedIcon from '@mui/icons-material/Speed';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SecurityIcon from '@mui/icons-material/Security';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';

const categoryIcons: Record<string, typeof BuildIcon> = {
  under_hood: BuildIcon,
  brakes: SpeedIcon,
  tires: TireRepairIcon,
  suspension: AcUnitIcon,
  electrical: LightbulbIcon,
  safety: SecurityIcon,
  exterior: DirectionsCarFilledIcon,
  interior: AirlineSeatReclineNormalIcon,
};

// categoryLabels moved inside component for i18n support

// Category with items interface
interface CategoryGroup {
  id: string;
  name: string;
  items: InspectionItem[];
}

interface MobileInspectionViewProps {
  inspectionId?: string;
}

export default function MobileInspectionView({ inspectionId: propId }: MobileInspectionViewProps) {
  const { id: paramId } = useParams<{ id: string }>();
  const { t } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();

  const categoryLabels: Record<string, string> = useMemo(() => ({
    under_hood: t('mobile.category.underHood'),
    brakes: t('mobile.category.brakes'),
    tires: t('mobile.category.tires'),
    suspension: t('mobile.category.suspension'),
    electrical: t('mobile.category.electrical'),
    safety: t('mobile.category.safety'),
    exterior: t('mobile.category.exterior'),
    interior: t('mobile.category.interior'),
  }), [t]);

  const inspectionId = propId || paramId;
  const { data: inspection, isLoading, error } = useInspection(inspectionId ?? '');
  const updateCondition = useUpdateItemCondition();
  const addPhoto = useAddPhoto();

  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);

  // Group items by category
  const categories = useMemo<CategoryGroup[]>(() => {
    if (!inspection?.items) return [];

    const grouped = new Map<string, InspectionItem[]>();

    for (const item of inspection.items) {
      const categoryId = item.categoryId || 'other';
      if (!grouped.has(categoryId)) {
        grouped.set(categoryId, []);
      }
      grouped.get(categoryId)!.push(item);
    }

    // Convert to array and sort
    return Array.from(grouped.entries())
      .map(([id, items]) => ({
        id,
        name: categoryLabels[id] || items[0]?.categoryName || id,
        items: items.sort((a, b) => a.sortOrder - b.sortOrder),
      }))
      .sort((a, b) => {
        const order = Object.keys(categoryLabels);
        return order.indexOf(a.id) - order.indexOf(b.id);
      });
  }, [inspection?.items, categoryLabels]);

  // Calculate progress
  const progress = useMemo(() => {
    if (!inspection?.items) return { completed: 0, total: 0, percent: 0 };

    let completed = 0;
    const total = inspection.items.length;

    for (const item of inspection.items) {
      if (item.condition) completed++;
    }

    return {
      completed,
      total,
      percent: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [inspection?.items]);

  // Count issues per category
  const categoryStats = useMemo(() => {
    const stats = new Map<string, { issues: number; total: number }>();

    for (const category of categories) {
      let issues = 0;
      for (const item of category.items) {
        if (item.condition === 'poor' || item.condition === 'critical') {
          issues++;
        }
      }
      stats.set(category.id, { issues, total: category.items.length });
    }

    return stats;
  }, [categories]);

  const currentCategory = categories[currentCategoryIndex];
  const isEditable = inspection?.status === 'draft' || inspection?.status === 'in_progress';

  // Handle condition change
  const handleConditionChange = useCallback(
    async (itemId: string, condition: InspectionItemCondition) => {
      if (!inspectionId) return;

      try {
        await updateCondition.mutateAsync({
          inspectionId,
          itemId,
          condition,
        });
        notify.success(t('mobile.saved'), t('mobile.conditionUpdated'));
      } catch (error) {
        notify.error(tc('error'), describeApiError(error, tc, { fallback: t('mobile.updateFailed') }));
      }
    },
    [inspectionId, updateCondition, notify, t, tc]
  );

  // Handle photo capture
  const handlePhotoCapture = useCallback(
    async (itemId: string, file: File) => {
      if (!inspectionId) return;

      try {
        await addPhoto.mutateAsync({
          inspectionId,
          itemId,
          file,
        });
        notify.success(t('mobile.photoAdded'), t('mobile.photoSaved'));
      } catch {
        notify.error(tc('error'), t('mobile.photoAddFailed'));
      }
    },
    [inspectionId, addPhoto, notify, t, tc]
  );

  // Toggle item expansion
  const toggleItem = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Navigate categories
  const goToPrevCategory = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(currentCategoryIndex - 1);
    }
  };

  const goToNextCategory = () => {
    if (currentCategoryIndex < categories.length - 1) {
      setCurrentCategoryIndex(currentCategoryIndex + 1);
    }
  };

  // Handle swipe gestures (simple touch handling)
  const [touchStart, setTouchStart] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      setTouchStart(touch.clientX);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;

    const touch = e.changedTouches[0];
    if (!touch) {
      setTouchStart(null);
      return;
    }

    const touchEnd = touch.clientX;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > 100) {
      if (diff > 0) {
        goToNextCategory();
      } else {
        goToPrevCategory();
      }
    }

    setTouchStart(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }} color="text.secondary">
          {t('mobile.loadingInspection')}
        </Typography>
      </Box>
    );
  }

  if (error || !inspection) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">{t('mobile.notFound')}</Typography>
        <Button onClick={() => navigate('/inspections')} sx={{ mt: 2 }}>
          {tc('back')}
        </Button>
      </Box>
    );
  }

  const CategoryIcon = currentCategory ? categoryIcons[currentCategory.id] || BuildIcon : BuildIcon;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        backgroundColor: 'background.default',
      }}
    >
      {/* Header */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <Tooltip title={tc('back')}>
            <IconButton edge="start" onClick={() => navigate('/inspections')} aria-label={tc('back')} sx={{ minWidth: 44, minHeight: 44 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DirectionsCarIcon fontSize="small" color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                {inspection.vehicle?.registrationNumber || t('mobile.inspection')}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {inspection.vehicle?.make} {inspection.vehicle?.model}
            </Typography>
          </Box>

          <Tooltip title={tc('moreOptions')}>
            <IconButton onClick={() => setMenuOpen(true)} aria-label={tc('moreOptions')} sx={{ minWidth: 44, minHeight: 44 }}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>

        {/* Progress Bar */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('mobile.progress')}
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {progress.completed}/{progress.total} ({progress.percent}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress.percent}
            sx={{ height: 6, borderRadius: 1 }}
          />
        </Box>
      </AppBar>

      {/* Category Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Tabs
          value={currentCategoryIndex}
          onChange={(_, v) => setCurrentCategoryIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 2,
              py: 1.5,
            },
          }}
        >
          {categories.map((category) => {
            const stats = categoryStats.get(category.id);
            const Icon = categoryIcons[category.id] || BuildIcon;

            return (
              <Tab
                key={category.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Icon fontSize="small" />
                    <Typography variant="caption">{category.name}</Typography>
                    {stats && stats.issues > 0 && (
                      <Chip
                        size="small"
                        label={stats.issues}
                        color="error"
                        sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                      />
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Category Navigation Header */}
      {currentCategory && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            backgroundColor: 'primary.50',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tooltip title={t('mobile.previousCategory')}>
            <span>
              <IconButton
                onClick={goToPrevCategory}
                disabled={currentCategoryIndex === 0}
                sx={{ touchAction: 'manipulation', minWidth: 44, minHeight: 44 }}
                aria-label={t('mobile.previousCategory')}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
              <CategoryIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {currentCategory.name}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {currentCategoryIndex + 1} / {categories.length}
            </Typography>
          </Box>

          <Tooltip title={t('mobile.nextCategory')}>
            <span>
              <IconButton
                onClick={goToNextCategory}
                disabled={currentCategoryIndex >= categories.length - 1}
                sx={{ touchAction: 'manipulation', minWidth: 44, minHeight: 44 }}
                aria-label={t('mobile.nextCategory')}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Items List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {currentCategory?.items.map((item) => {
          const isExpanded = expandedItems.has(item.id);
          const hasIssue = item.condition === 'poor' || item.condition === 'critical';
          const photos = inspection.photos?.filter((p) => p.inspectionItemId === item.id) || [];

          return (
            <Paper
              key={item.id}
              elevation={0}
              sx={{
                mb: 1,
                mx: 1.5,
                mt: 1,
                borderRadius: 2,
                border: 1,
                borderColor: hasIssue ? 'error.300' : 'divider',
                backgroundColor: hasIssue ? 'error.50' : 'background.paper',
                overflow: 'hidden',
              }}
            >
              {/* Item Header */}
              <Box
                onClick={() => toggleItem(item.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                  '&:active': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ConditionBadge condition={item.condition} />

                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={hasIssue ? 600 : 500}>
                    {item.itemName}
                  </Typography>
                  {item.itemDescription && (
                    <Typography variant="caption" color="text.secondary">
                      {item.itemDescription}
                    </Typography>
                  )}
                </Box>

                {/* Indicators */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {photos.length > 0 && (
                    <PhotoPreview
                      photos={photos.map((p) => ({
                        id: p.id,
                        url: p.fileUrl,
                        thumbnailUrl: p.thumbnailUrl ?? undefined,
                        caption: p.caption ?? undefined,
                      }))}
                    />
                  )}
                  <Tooltip title={isExpanded ? tc('collapse') : tc('expand')}>
                    <IconButton size="small" aria-label={isExpanded ? tc('collapse') : tc('expand')} sx={{ minWidth: 44, minHeight: 44 }}>
                      <ExpandMoreIcon
                        sx={{
                          transform: isExpanded ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.2s',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Expanded Content */}
              <Collapse in={isExpanded}>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Divider sx={{ mb: 2 }} />

                  {/* Condition Selector */}
                  {isEditable && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {t('mobile.selectCondition')}
                      </Typography>
                      <TouchConditionSelector
                        value={item.condition}
                        onChange={(condition) => handleConditionChange(item.id, condition)}
                        layout="grid"
                      />
                    </Box>
                  )}

                  {/* Notes */}
                  {item.notes && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        {t('mobile.notes')}
                      </Typography>
                      <Typography variant="body2">{item.notes}</Typography>
                    </Box>
                  )}

                  {/* Photos */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {t('mobile.photos')}
                    </Typography>
                    <PhotoCapture
                      photos={photos.map((p) => ({
                        id: p.id,
                        url: p.fileUrl,
                        thumbnailUrl: p.thumbnailUrl ?? undefined,
                        caption: p.caption ?? undefined,
                      }))}
                      onCapture={isEditable ? (file) => handlePhotoCapture(item.id, file) : undefined}
                      disabled={!isEditable}
                      itemName={item.itemName}
                    />
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>

      {/* Bottom Actions */}
      {isEditable && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            display: 'flex',
            gap: 1.5,
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          {inspection.status === 'in_progress' && (
            <Button
              variant="contained"
              color="success"
              fullWidth
              size="large"
              startIcon={<CheckCircleIcon />}
              sx={{ minHeight: 48 }}
            >
              {t('mobile.complete')}
            </Button>
          )}
          {inspection.status === 'completed' && (
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<SendIcon />}
              sx={{ minHeight: 48 }}
            >
              {t('mobile.sendToCustomer')}
            </Button>
          )}
        </Box>
      )}

      {/* Menu Drawer */}
      <SwipeableDrawer
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onOpen={() => setMenuOpen(true)}
        PaperProps={{
          sx: { width: '80%', maxWidth: 320 },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('mobile.menu')}
          </Typography>
        </Box>
        <Divider />
        <List>
          <ListItem component="button" onClick={() => navigate(`/inspections/${inspectionId}`)}>
            <ListItemIcon>
              <DirectionsCarIcon />
            </ListItemIcon>
            <ListItemText primary={t('mobile.fullView')} secondary={t('mobile.showDesktopVersion')} />
          </ListItem>
          <ListItem component="button">
            <ListItemIcon>
              <SaveIcon />
            </ListItemIcon>
            <ListItemText primary={tc('save')} secondary={t('mobile.saveChanges')} />
          </ListItem>
          <Divider />
          <ListItem component="button">
            <ListItemIcon>
              <WarningIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('mobile.findings')}
              secondary={t('mobile.issuesCount', { count: Array.from(categoryStats.values()).reduce((sum, s) => sum + s.issues, 0) })}
            />
          </ListItem>
        </List>
      </SwipeableDrawer>
    </Box>
  );
}
